<template>
  <div ref="container" class="card p-shadow-app telematics-detail">
    <Toast :style="{ width: $isMobile() ? '85vw' : '' }" />
    <div class="detail-toolbar">
      <div>
        <Button
          icon="pi pi-arrow-left"
          label="Voltar para lista"
          class="p-button-sm p-button-text"
          @click="backToList"
        />
        <h1>Veículo {{ title }}</h1>
        <p class="detail-subtitle">VIN {{ vin }}<span v-if="vehicle"> | {{ vehicle.brand }} {{ vehicle.model }}</span></p>
        <router-link v-if="fleetRoute" :to="fleetRoute">Abrir ficha da frota</router-link>
        <p v-if="vehicleLoading" role="status">A procurar veículo na frota...</p>
        <p v-else-if="vehicleError" role="status">{{ vehicleError }}</p>
        <p v-else-if="!vehicle || !vehicle.id">Veículo sem associação na frota local.</p>
      </div>
      <Button label="Atualizar" icon="pi pi-refresh" class="p-button-outlined" :disabled="activeLoading" @click="refresh" />
    </div>
    <TabView :activeIndex="activeTab" @tab-change="onTabChange">
      <TabPanel header="Visão geral">
        <p v-if="twinLoading" role="status">A carregar dados do veículo...</p>
        <Message v-else-if="twinError" severity="error" :closable="false">{{ twinError }}</Message>
        <p v-else-if="!digitalTwinGroups.length">Sem dados de telemática.</p>
        <template v-else>
          <p class="data-note">Último evento: {{ formatTimestamp(digitalTwin.lastEvent && digitalTwin.lastEvent.timestampEvent) }} · Cada valor corresponde à data da respetiva leitura.</p>
          <div class="vehicle-summary">
            <KpiCard v-for="metric in summaryMetrics" :key="metric.path" :label="metric.label" :value="metric.value" :detail="metric.detail" :icon="metric.icon" :tone="metric.tone" />
          </div>
          <Message v-if="vehicleAlerts.length" severity="warn" :closable="false">
            <strong>Alertas nas últimas leituras</strong>
            <div v-for="alert in vehicleAlerts" :key="alert.path">{{ alert.label }} · {{ formatTimestamp(alert.timestamp) }}</div>
          </Message>
          <div class="overview-layout">
          <section class="vehicle-data" aria-label="Dados e diagnósticos do veículo">
          <h2>Dados do veículo</h2>
          <p class="data-note">Abra uma secção para consultar as leituras e as respetivas datas.</p>
          <Accordion :multiple="true" expandIcon="pi-plus" collapseIcon="pi-minus">
          <AccordionTab v-for="group in digitalTwinGroups" :key="group.key" :header="group.title" class="p-mb-3">
            <p v-if="!group.cards.length && !group.items.length">Sem dados.</p>
            <div v-if="group.cards.length" class="twin-cards">
              <section v-for="card in group.cards" :key="card.key" class="twin-card">
                <h3>{{ card.title }}</h3>
                <div v-for="item in card.items" :key="item.path" class="twin-card-row">
                  <span>{{ item.label }}</span><strong>{{ item.value }}</strong>
                  <small v-if="item.timestamp">Leitura: {{ formatTimestamp(item.timestamp) }}</small>
                </div>
              </section>
            </div>
            <div v-else-if="group.items.length">
              <div v-for="item in group.items" :key="item.path" class="twin-card-row">
                <span>{{ item.label }}</span><strong>{{ item.value }}</strong>
                <small v-if="item.timestamp">Leitura: {{ formatTimestamp(item.timestamp) }}</small>
              </div>
            </div>
          </AccordionTab>
          </Accordion>
          </section>
          <aside class="location-panel" aria-label="Última localização do veículo">
            <div class="location-heading"><i class="pi pi-map-marker" aria-hidden="true"></i><h2>Última localização</h2></div>
            <p>{{ digitalTwin.address && digitalTwin.address.formattedAddress || 'Morada não disponível' }}</p>
            <p class="data-note">GPS: {{ formatTimestamp(gpsTimestamp) }}</p>
            <div v-if="gpsCoordinates" class="map-container" aria-label="Mapa da última localização GPS">
              <LMap ref="gpsMap" :zoom="15" :center="gpsCoordinates" @ready="resizeMaps">
                <LTileLayer :url="tileUrl" :attribution="tileAttribution" />
                <LCircleMarker :lat-lng="gpsCoordinates" :radius="8" color="#3969ac" :fillOpacity="0.9">
                  <LPopup>{{ title }}<br />GPS: {{ formatTimestamp(gpsTimestamp) }}</LPopup>
                </LCircleMarker>
              </LMap>
            </div>
            <p v-else>{{ privacyActive ? 'Modo de privacidade ativo.' : 'Sem coordenadas GPS válidas.' }}</p>
            <div v-if="gpsCoordinates" class="location-coordinates"><span>Latitude {{ formatRawValue('gpsData.coordinates.latitude', gpsCoordinates[0]) }}</span><span>Longitude {{ formatRawValue('gpsData.coordinates.longitude', gpsCoordinates[1]) }}</span></div>
            <p class="data-note">Última posição comunicada, não localização em tempo real.</p>
          </aside>
          </div>
        </template>
      </TabPanel>
      <TabPanel header="Viagens">
        <form class="trip-filters" @submit.prevent="loadTrips">
          <div>
            <label for="tripsMode">Período</label>
            <Dropdown id="tripsMode" v-model="tripsMode" :options="tripModes" optionLabel="label" optionValue="value" />
          </div>
          <div v-if="tripsMode === 'day'">
            <label for="tripsDay">Dia</label>
            <Calendar id="tripsDay" v-model="tripsDay" dateFormat="dd/mm/yy" :showIcon="true" :maxDate="today" @show="updateDateLimit" />
          </div>
          <template v-else>
            <div>
              <label for="tripsFrom">De</label>
              <Calendar id="tripsFrom" v-model="tripsFrom" dateFormat="dd/mm/yy" :showTime="true" :showIcon="true" :maxDate="today" @show="updateDateLimit" />
            </div>
            <div>
              <label for="tripsTo">Até</label>
              <Calendar id="tripsTo" v-model="tripsTo" dateFormat="dd/mm/yy" :showTime="true" :showIcon="true" :maxDate="today" @show="updateDateLimit" />
            </div>
          </template>
          <Button type="submit" label="Pesquisar" icon="pi pi-search" :disabled="tripsLoading" />
        </form>
        <p class="data-note">O período selecionado usa a hora de Lisboa.</p>
        <Message v-if="tripsError" severity="error" :closable="false">{{ tripsError }}</Message>
        <p v-if="tripsLoading" role="status">A carregar viagens...</p>
        <p v-else-if="!tripsSearched">Escolha um dia ou intervalo e pesquise as viagens.</p>
        <p v-else-if="!trips.length && !tripsError">Sem viagens neste período.</p>
        <div v-if="trips.length" class="vehicle-summary trip-summary">
          <KpiCard label="Viagens no período" :value="trips.length" icon="pi pi-car" />
          <KpiCard label="Distância percorrida" :value="tripSummary.distance" :detail="tripSummary.distanceDetail" icon="pi pi-directions" tone="blue" />
          <KpiCard label="Duração das viagens" :value="tripSummary.duration" :detail="tripSummary.durationDetail" icon="pi pi-clock" tone="purple" />
        </div>
        <div v-if="trips.length" class="trip-layout">
          <section aria-label="Lista de viagens">
            <div class="detail-toolbar p-mb-2">
              <Button label="Selecionar todas" :disabled="tripsLoading" @click="selectAllTrips" />
              <Button label="Limpar seleção" class="p-button-text" :disabled="!selectedTrips.length" @click="clearSelection" />
            </div>
            <p role="status">{{ selectedTrips.length }} de {{ trips.length }} viagens selecionadas.</p>
            <p v-if="driversLoading" role="status">A procurar condutores...</p>
            <p v-if="driversError" role="status">{{ driversError }}</p>
            <div class="trip-list">
              <label v-for="(trip, index) in trips" :key="index" class="trip-card" :class="{ selected: selectedTrips.includes(trip) }">
                <input type="checkbox" :checked="selectedTrips.includes(trip)" @change="toggleTrip(trip)" />
                <span>
                  <strong>{{ formatTimestamp(trip.dateBegin) }} - {{ formatTimestamp(trip.dateEnd) }}</strong>
                  <span>Partida: {{ trip.formattedAddressBegin || (trip.addressBegin && trip.addressBegin.formattedAddress) || '-' }}</span>
                  <span>Chegada: {{ trip.formattedAddressEnd || (trip.addressEnd && trip.addressEnd.formattedAddress) || '-' }}</span>
                  <span>Distância: {{ formatMetric(trip.drivingMileage, 'km') }} | Velocidade Média: {{ formatMetric(trip.meanDrivingSpeed, 'km/h') }}</span>
                  <span>Duração: {{ formatDuration(Date.parse(trip.dateEnd) - Date.parse(trip.dateBegin)) }}</span>
                  <span v-if="trip.conductorName">Condutor: {{ trip.conductorName }}</span>
                </span>
              </label>
            </div>
          </section>
          <section aria-label="Percursos das viagens">
            <p v-if="routesLoading" role="status">A carregar percursos...</p>
            <Message v-if="routesError" severity="error" :closable="false">{{ routesError }}</Message>
            <p v-if="!selectedTrips.length">Selecione uma ou mais viagens para ver os percursos.</p>
            <p v-else-if="!routesLoading && !routes.length && !routesError">Sem coordenadas válidas para estas viagens.</p>
            <p v-if="routesMissing">{{ routesMissing }} viagens sem percurso GPS disponível.</p>
            <div v-for="(route, index) in routes" :key="route.key" class="route-legend">
              <span class="route-color" :style="{ backgroundColor: tripColor(index) }"></span>
              {{ formatTimestamp(route.trip.dateBegin) }} - {{ formatTimestamp(route.trip.dateEnd) }}
              <span v-if="route.endpointsOnly"> (apenas extremos, não representa o percurso GPS)</span>
            </div>
            <div class="map-container trips-map" aria-label="Mapa dos percursos selecionados">
              <LMap ref="tripsMap" :zoom="6" :center="[39.5, -8]" @ready="resizeMaps">
                <LTileLayer :url="tileUrl" :attribution="tileAttribution" />
                <template v-for="(route, index) in routes">
                  <LPolyline v-if="!route.endpointsOnly" :key="route.key + '-line'" :lat-lngs="route.points" :color="tripColor(index)" />
                  <LCircleMarker :key="route.key + '-start'" :lat-lng="route.points[0]" :radius="6" :color="tripColor(index)">
                    <LPopup>Partida: {{ formatTimestamp(route.trip.dateBegin) }}</LPopup>
                  </LCircleMarker>
                  <LCircleMarker :key="route.key + '-end'" :lat-lng="route.points[route.points.length - 1]" :radius="6" :color="tripColor(index)">
                    <LPopup>Chegada: {{ formatTimestamp(route.trip.dateEnd) }}</LPopup>
                  </LCircleMarker>
                </template>
              </LMap>
            </div>
          </section>
        </div>
      </TabPanel>
    </TabView>
  </div>
</template>

<script>
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import { LMap, LTileLayer, LCircleMarker, LPolyline, LPopup } from "vue2-leaflet";
import { latLngBounds } from "leaflet";
import TargaTelematicsService from "../services/targaTelematics.service";
import FleetService from "../services/fleet.service";
import { getOnlyDate, getOnlyTime } from "../helpers/helpers";
import KpiCard from "../components/dashboards/KpiCard.vue";
import { coordinates, reading, formatReading, formatTimestamp, formatMetric, calendarDate, lisbonIso } from "../helpers/targaTelematics";

const groupTitles = {
  vin: "VIN", lastEvent: "Último Evento", address: "Última Localização", gpsData: "GPS", gsmData: "GSM",
  deviceDiagnostics: "Diagnóstico do Dispositivo", vehicleDiagnostics: "Diagnóstico do Veículo", privacy: "Privacidade",
  battery: "Bateria", engine: "Motor", fuel: "Combustível", motion: "Movimento", maintenance: "Manutenção",
  doors: "Portas", tires: "Pneus", transmission: "Transmissão", tractionBattery: "Bateria de Tração",
};

export default {
  name: "TargaTelematicsVehicleDetails",
  components: { Accordion, AccordionTab, TabView, TabPanel, KpiCard, LMap, LTileLayer, LCircleMarker, LPolyline, LPopup },
  data() {
    return {
      vin: this.$route.params.vin,
      plate: this.$route.query.plate || "",
      vehicle: null, vehicleLoading: false, vehicleError: "", vehicleSequence: 0,
      digitalTwin: null, twinLoading: false, twinError: "", twinSequence: 0,
      activeTab: 0,
      today: calendarDate(), tripsMode: "day", tripsDay: calendarDate(), tripsFrom: null, tripsTo: null,
      tripModes: [{ label: "Dia", value: "day" }, { label: "Intervalo", value: "range" }],
      trips: [], tripsLoading: false, tripsError: "", tripsSearched: false, tripsSequence: 0,
      selectedTrips: [], routes: [], routesLoading: false, routesError: "", routesMissing: 0, routesSequence: 0,
      driversLoading: false, driversError: "",
      tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      tileAttribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    };
  },
  computed: {
    title() {
      return (this.vehicle && this.vehicle.registration) || this.plate || this.vin;
    },
    fleetRoute() {
      return this.vehicle && this.vehicle.id && this.can("seeFleetVehicleDetails")
        ? { name: "vehicle-details", params: { id: this.vehicle.id } } : null;
    },
    activeLoading() {
      return this.vehicleLoading || (this.activeTab === 0 ? this.twinLoading : this.tripsLoading || this.routesLoading);
    },
    gpsCoordinates() {
      return this.privacyActive ? null : this.coordinate(this.digitalTwin && this.digitalTwin.gpsData && this.digitalTwin.gpsData.coordinates);
    },
    privacyActive() { return reading(this.digitalTwin, "privacy.privacyOn").value === true; },
    gpsTimestamp() { return this.digitalTwin && this.digitalTwin.gpsData && this.digitalTwin.gpsData.coordinates && this.digitalTwin.gpsData.coordinates.timestamp; },
    summaryMetrics() {
      const odometerPath = reading(this.digitalTwin, "vehicleDiagnostics.odometer").value != null ? "vehicleDiagnostics.odometer" : "gpsData.odometer";
      return [
        { path: "gpsData.speed", label: "Velocidade GPS", icon: "pi pi-compass", tone: "blue" },
        { path: odometerPath, label: odometerPath.startsWith("gpsData") ? "Odómetro GPS" : "Odómetro", icon: "pi pi-directions", tone: "teal" },
        { path: "vehicleDiagnostics.fuel.tankPercentage", label: "Combustível", icon: "pi pi-chart-pie", tone: "amber" },
        { path: "vehicleDiagnostics.tractionBattery.batteryPercentage", label: "Bateria do veículo", icon: "pi pi-bolt", tone: "green" },
      ].map((metric) => {
        const measure = reading(this.digitalTwin, metric.path);
        return { ...metric, value: this.formatRawValue(metric.path, measure.value), detail: measure.timestamp ? `Leitura: ${this.formatTimestamp(measure.timestamp)}` : "Sem data de leitura" };
      });
    },
    vehicleAlerts() {
      return [
        ["vehicleDiagnostics.engine.engineFault", "Avaria do motor"],
        ["vehicleDiagnostics.engine.checkEngineLightOn", "Luz de avaria do motor acesa"],
        ["vehicleDiagnostics.battery.levelLow", "Bateria com carga baixa"],
        ["vehicleDiagnostics.maintenance.oilToChange", "Mudança de óleo necessária"],
        ["vehicleDiagnostics.maintenance.genericAlarm", "Aviso de manutenção"],
        ...["frontLeftTire", "frontRightTire", "rearLeftTire", "rearRightTire"].map((tire) => [`vehicleDiagnostics.tires.${tire}.lowTirePressure`, `Pressão baixa: ${this.translatePathSegment(tire)}`]),
      ].flatMap(([path, label]) => {
        const measure = reading(this.digitalTwin, path);
        return measure.value === true ? [{ path, label, timestamp: measure.timestamp }] : [];
      });
    },
    tripSummary() {
      const distances = this.trips.filter((trip) => typeof trip.drivingMileage === "number" && Number.isFinite(trip.drivingMileage));
      const durations = this.trips.map((trip) => Date.parse(trip.dateEnd) - Date.parse(trip.dateBegin)).filter((duration) => Number.isFinite(duration) && duration >= 0);
      return {
        distance: distances.length ? this.formatMetric(distances.reduce((total, trip) => total + trip.drivingMileage, 0), "km") : "-",
        distanceDetail: distances.length === this.trips.length ? "Total das viagens apresentadas" : "Total parcial: existem viagens sem distância",
        duration: durations.length ? this.formatDuration(durations.reduce((total, duration) => total + duration, 0)) : "-",
        durationDetail: durations.length === this.trips.length ? "Tempo decorrido entre partida e chegada" : "Total parcial: existem viagens sem datas válidas",
      };
    },
    digitalTwinGroups() {
      if (!this.digitalTwin) return [];
      const order = ["vehicleDiagnostics", "gpsData", "deviceDiagnostics", "gsmData", "address", "lastEvent", "privacy", "vin"];
      order.push(...Object.keys(this.digitalTwin).filter((key) => !order.includes(key)));
      return order.filter((key) => this.digitalTwin[key] !== undefined).map((key) => {
        const data = this.digitalTwin[key];
        const items = this.flattenDeep(data, key);
        const cards = this.buildGroupCards(key, items);
        return { key, title: groupTitles[key] || key, items, cards };
      });
    },
  },
  watch: {
    tripsMode: "resetTrips",
    tripsDay: "resetTrips",
    tripsFrom: "resetTrips",
    tripsTo: "resetTrips",
  },
  created() {
    this.loadVehicle();
    this.loadDigitalTwin();
  },
  mounted() {
    window.addEventListener("resize", this.resizeMaps);
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.resizeMaps());
      this.resizeObserver.observe(this.$refs.container);
    }
  },
  activated() {
    this.updateDateLimit();
    this.resizeMaps();
  },
  beforeDestroy() {
    this.vehicleSequence++;
    this.twinSequence++;
    this.resetTrips();
    window.removeEventListener("resize", this.resizeMaps);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    formatRawValue: formatReading,
    formatTimestamp,
    formatMetric,
    coordinate: coordinates,
    updateDateLimit() { this.today = calendarDate(); },
    formatDuration(milliseconds) {
      if (!Number.isFinite(milliseconds) || milliseconds < 0) return "-";
      const minutes = Math.round(milliseconds / 60000);
      return `${Math.floor(minutes / 60).toLocaleString("pt-PT")} h ${minutes % 60} min`;
    },
    notify(message) {
      this.$toast.add({ severity: "error", summary: "Targa Telematics", detail: message, life: 5000 });
    },
    async loadVehicle() {
      const sequence = ++this.vehicleSequence;
      this.vehicleLoading = true;
      this.vehicle = null;
      this.vehicleError = "";
      try {
        const vehicle = await TargaTelematicsService.getVehicleByVin(this.vin);
        if (sequence !== this.vehicleSequence) return;
        if (vehicle !== null && (typeof vehicle !== "object" || Array.isArray(vehicle))) throw new Error("Invalid local vehicle");
        this.vehicle = vehicle;
        if (!this.tripsLoading && !this.driversLoading) this.enrichDrivers(this.tripsSequence);
      } catch (error) {
        if (sequence !== this.vehicleSequence || (error.response && error.response.status === 404)) return;
        this.vehicleError = "Não foi possível consultar a frota local. A telemática continua disponível.";
        this.notify(this.vehicleError);
      } finally {
        if (sequence === this.vehicleSequence) this.vehicleLoading = false;
      }
    },
    async loadDigitalTwin() {
      const sequence = ++this.twinSequence;
      this.twinLoading = true;
      this.twinError = "";
      this.digitalTwin = null;
      try {
        const twin = await TargaTelematicsService.getVehicleDigitalTwin(this.vin);
        if (sequence !== this.twinSequence) return;
        if (twin !== null && (typeof twin !== "object" || Array.isArray(twin))) throw new Error("Invalid Digital Twin");
        this.digitalTwin = twin;
      } catch (error) {
        if (sequence !== this.twinSequence) return;
        this.twinError = "Não foi possível carregar os dados de telemática. Tente atualizar.";
        this.notify(this.twinError);
      } finally {
        if (sequence === this.twinSequence) {
          this.twinLoading = false;
          this.resizeMaps();
        }
      }
    },
    refresh() {
      this.loadVehicle();
      if (this.activeTab === 0) this.loadDigitalTwin();
      else this.loadTrips();
    },
    onTabChange(event) {
      this.activeTab = event.index;
      if (event.index === 1 && !this.tripsSearched && !this.tripsLoading) this.loadTrips();
      this.resizeMaps();
    },
    resetTrips() {
      this.tripsSequence++;
      this.trips = [];
      this.tripsLoading = false;
      this.tripsError = "";
      this.tripsSearched = false;
      this.driversLoading = false;
      this.driversError = "";
      this.clearSelection();
    },
    tripParams() {
      let from = this.tripsFrom;
      let to = this.tripsTo;
      if (this.tripsMode === "day") {
        if (!(this.tripsDay instanceof Date) || !Number.isFinite(this.tripsDay.getTime())) return null;
        from = new Date(this.tripsDay);
        from.setHours(0, 0, 0, 0);
        to = new Date(this.tripsDay);
        to.setHours(23, 59, 59, 999);
      }
      if (!(from instanceof Date) || !(to instanceof Date) || !Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) || from > to) return null;
      const fromIso = lisbonIso(from);
      const toIso = lisbonIso(to);
      if (!fromIso || !toIso || fromIso > toIso) return null;
      return { from: fromIso, to: toIso, measurementSystem: "METRIC" };
    },
    parseTrips(response) {
      if (response === null) return [];
      const trips = Array.isArray(response) ? response : response && response.trips;
      if (!Array.isArray(trips)) throw new Error("Invalid trip list");
      return trips.filter((trip) => trip && typeof trip === "object").map((trip) => {
        const factor = trip.measurementSystem === "IMPERIAL" ? 1.609344 : 1;
        return { ...trip,
          drivingMileage: typeof trip.drivingMileage === "number" && Number.isFinite(trip.drivingMileage) ? trip.drivingMileage * factor : null,
          meanDrivingSpeed: typeof trip.meanDrivingSpeed === "number" && Number.isFinite(trip.meanDrivingSpeed) ? trip.meanDrivingSpeed * factor : null,
        };
      }).sort((a, b) => Date.parse(b.dateBegin) - Date.parse(a.dateBegin));
    },
    async loadTrips() {
      const params = this.tripParams();
      this.resetTrips();
      if (!params) {
        this.tripsError = "Escolha datas válidas; o início não pode ser posterior ao fim.";
        this.notify(this.tripsError);
        return;
      }
      const sequence = this.tripsSequence;
      this.tripsLoading = true;
      this.tripsSearched = true;
      try {
        const response = await TargaTelematicsService.getVehicleTrips(this.vin, params);
        if (sequence !== this.tripsSequence) return;
        this.trips = this.parseTrips(response);
        this.enrichDrivers(sequence);
      } catch (error) {
        if (sequence !== this.tripsSequence) return;
        this.tripsError = "Não foi possível carregar as viagens. Tente pesquisar novamente.";
        this.notify(this.tripsError);
      } finally {
        if (sequence === this.tripsSequence) this.tripsLoading = false;
      }
    },
    async enrichDrivers(sequence) {
      const vehicleId = this.vehicle && this.vehicle.id;
      if (!vehicleId || !this.can("seeFleetVehicleDetails") || !this.trips.length) return;
      this.driversLoading = true;
      const trips = this.trips.slice();
      try {
        for (let index = 0; index < trips.length; index += 5) {
          if (sequence !== this.tripsSequence) return;
          await Promise.all(trips.slice(index, index + 5).map(async (trip) => {
            const date = new Date(trip.dateBegin);
            if (!trip.dateBegin || !Number.isFinite(date.getTime())) return;
            const lisbonDate = calendarDate(date);
            const conductor = await FleetService.searchCondutorByVehicleDate(vehicleId, encodeURIComponent(`${getOnlyDate(lisbonDate)} ${getOnlyTime(lisbonDate)}`));
            if (sequence !== this.tripsSequence) return;
            if (conductor) this.$set(trip, "conductorName", conductor.username || "");
          }));
        }
      } catch (error) {
        if (sequence === this.tripsSequence) this.driversError = "Condutores indisponíveis; as viagens continuam disponíveis.";
      } finally {
        if (sequence === this.tripsSequence) this.driversLoading = false;
      }
    },
    clearSelection() {
      this.selectedTrips = [];
      this.routesSequence++;
      this.routes = [];
      this.routesLoading = false;
      this.routesError = "";
      this.routesMissing = 0;
      this.resizeMaps();
    },
    toggleTrip(trip) {
      this.selectedTrips = this.selectedTrips.includes(trip)
        ? this.selectedTrips.filter((selected) => selected !== trip) : [...this.selectedTrips, trip];
      this.loadRoutes();
    },
    selectAllTrips() {
      this.selectedTrips = this.trips.slice();
      this.loadRoutes();
    },
    async loadRoutes() {
      const sequence = ++this.routesSequence;
      const trips = this.selectedTrips.slice();
      this.routes = [];
      this.routesError = "";
      this.routesMissing = 0;
      this.routesLoading = trips.length > 0;
      this.resizeMaps();
      if (!trips.length) return;
      try {
        const routes = [];
        let unavailable = 0;
        for (let index = 0; index < trips.length; index += 5) {
          if (sequence !== this.routesSequence) return;
          const batch = await Promise.all(trips.slice(index, index + 5).map(async (trip, offset) => {
            if (!trip.dateBegin || !trip.dateEnd || !Number.isFinite(Date.parse(trip.dateBegin)) || !Number.isFinite(Date.parse(trip.dateEnd)) || Date.parse(trip.dateBegin) > Date.parse(trip.dateEnd)) { unavailable++; return []; }
            let parsed = this.parseRoutes(trip);
            if (!parsed.some((route) => !route.endpointsOnly)) {
              try {
                const detail = await TargaTelematicsService.getVehicleTripDetails(this.vin, {
                  from: trip.dateBegin, measurementSystem: "METRIC",
                });
                const detailed = this.parseRoutes(detail);
                if (detailed.length) parsed = detailed;
              } catch (error) {
                unavailable++;
              }
            }
            return parsed.map((route, part) => ({ ...route, trip, key: `${index + offset}-${part}` }));
          }));
          routes.push(...batch.flat());
        }
        if (sequence !== this.routesSequence) return;
        this.routes = routes;
        this.routesMissing = trips.filter((trip) => !routes.some((route) => route.trip === trip && !route.endpointsOnly)).length;
        if (unavailable) this.routesError = `${unavailable} percursos não puderam ser consultados. Os percursos disponíveis continuam no mapa.`;
      } catch (error) {
        if (sequence !== this.routesSequence) return;
        this.routesError = "Não foi possível carregar os percursos. Volte a selecionar as viagens para tentar novamente.";
        this.notify(this.routesError);
      } finally {
        if (sequence === this.routesSequence) {
          this.routesLoading = false;
          this.resizeMaps();
        }
      }
    },
    decodePolyline(encoded) {
      if (typeof encoded !== "string") return [];
      let index = 0;
      let lat = 0;
      let lng = 0;
      const points = [];
      while (index < encoded.length) {
        const deltas = [];
        for (let axis = 0; axis < 2; axis++) {
          let result = 0;
          let shift = 0;
          let byte;
          do {
            if (index >= encoded.length || shift > 30) return [];
            byte = encoded.charCodeAt(index++) - 63;
            if (byte < 0 || byte > 63) return [];
            result += (byte & 31) * Math.pow(2, shift);
            shift += 5;
          } while (byte >= 32);
          deltas.push(result % 2 ? -(result + 1) / 2 : result / 2);
        }
        lat += deltas[0];
        lng += deltas[1];
        const point = this.coordinate([lat / 1e5, lng / 1e5]);
        if (!point) return [];
        points.push(point);
      }
      return points;
    },
    parseRoutes(response) {
      if (!response) return [];
      const items = Array.isArray(response) ? response : [response];
      return items.map((raw) => {
        if (!raw || typeof raw !== "object") throw new Error("Invalid trip details");
        const trip = raw.trip || raw;
        const route = trip.route || raw.route || {};
        const polyline = trip.polyline || raw.polyline || route.polyline;
        const encoded = typeof polyline === "string" ? polyline : polyline && polyline.points;
        const geometry = route.geometry || trip.geometry;
        const coordinates = Array.isArray(encoded) ? encoded : route.coordinates || trip.coordinates;
        let points = typeof encoded === "string" ? this.decodePolyline(encoded) : [];
        if (!points.length) {
          const source = coordinates || (geometry && geometry.type === "LineString" && geometry.coordinates);
          if (Array.isArray(source)) points = source.map((point) => this.coordinate(!coordinates && Array.isArray(point) ? [point[1], point[0]] : point));
          if (points.some((point) => !point)) points = [];
        }
        if (points.length >= 2) return { points, endpointsOnly: false };
        const start = trip.coordinatesBegin || (trip.start && trip.start.coordinates) || trip.startPosition || trip.startLocation || trip.start;
        const end = trip.coordinatesEnd || (trip.end && trip.end.coordinates) || trip.endPosition || trip.endLocation || trip.end;
        const endpoints = [this.coordinate(start), this.coordinate(end)];
        return endpoints.every(Boolean) ? { points: endpoints, endpointsOnly: true } : null;
      }).filter(Boolean);
    },
    flattenDeep(value, prefix) {
      const items = [];
      if (value === null || value === undefined) {
        if (prefix) items.push({ path: prefix, label: this.formatPath(prefix), value: "-" });
        return items;
      }
      if (Array.isArray(value)) {
        if (value.length === 0) { items.push({ path: prefix, label: this.formatPath(prefix), value: "-" }); return items; }
        value.forEach((item, index) => { items.push(...this.flattenDeep(item, prefix ? `${prefix}[${index}]` : `[${index}]`)); });
        return items;
      }
      if (typeof value === "object") {
        if (Object.prototype.hasOwnProperty.call(value, "value")) {
          items.push({ path: prefix, label: this.formatPath(prefix), value: this.formatRawValue(prefix, value.value), timestamp: value.timestamp });
          Object.keys(value).filter((key) => !["value", "timestamp"].includes(key)).forEach((key) => {
            items.push(...this.flattenDeep(value[key], `${prefix}.${key}`));
          });
        } else {
          Object.keys(value).forEach((key) => { items.push(...this.flattenDeep(value[key], prefix ? `${prefix}.${key}` : key)); });
        }
        return items;
      }
      items.push({ path: prefix, label: this.formatPath(prefix), value: this.formatRawValue(prefix, value) });
      return items;
    },
    formatPath(path) {
      if (!path) return "Valor";
      return path.split(".").map((part) => {
        const matches = part.match(/\[\d+\]/g) || [];
        const base = part.replace(/\[\d+\]/g, "");
        return this.translatePathSegment(base) + matches.join("");
      }).join(" / ");
    },
    translatePathSegment(segment) {
      if (!segment) return "";
      const labelMap = {
        value: "Valor", timestamp: "Data", timestampEvent: "Data do Evento", timestampRx: "Data de Receção",
        timestampTx: "Data de Envio", voltage: "Tensão", percentage: "Percentagem", levelLow: "Nível Baixo",
        onTimeFromDevice: "Tempo de Funcionamento (Dispositivo)", onTimeRealTime: "Tempo de Funcionamento (Tempo Real)",
        onTime: "Tempo de Funcionamento", temperature: "Temperatura", oilTemperature: "Temperatura do Óleo",
        checkEngineLightOn: "Luz do Motor", engineLock: "Bloqueio do Motor", dashboardOn: "Painel",
        remoteDashboardOn: "Painel Remoto", odometer: "Odómetro", odometerRealTime: "Odómetro (Tempo Real)",
        odometerTripA: "Distância parcial A", odometerEco: "Distância em modo económico", odometerNormal: "Distância em modo normal",
        odometerPower: "Distância em modo de potência", externalTemperature: "Temperatura exterior", tankLiters: "Combustível no depósito",
        tankPercentage: "Percentagem no Depósito", secondaryTankPercentage: "Percentagem Secundária",
        autonomy: "Autonomia", bearing: "Rumo", kmToService: "Distância até à revisão", daysToService: "Dias até à revisão",
        kmToOilChange: "Distância até à mudança de óleo", formattedAddress: "Morada", countryCode: "Código do país",
        country: "País", regionState: "Região/Estado", province: "Distrito", city: "Cidade", locality: "Localidade",
        postalCode: "Código Postal", street: "Rua", streetNumber: "Número", streetType: "Tipo de Rua",
        latitude: "Latitude", longitude: "Longitude", altitude: "Altitude", hdop: "HDOP",
        satellitesCount: "Satélites", coordinates: "Coordenadas", speedLimit: "Limite de Velocidade",
        driverDoor: "Porta do Condutor", passengerDoor: "Porta do Passageiro", rearLeftDoor: "Porta Traseira Esq.",
        rearRightDoor: "Porta Traseira Dir.", hood: "Capot", trunk: "Bagageira", sunroof: "Teto de abrir",
        open: "Aberta", batteryVoltage: "Tensão da Bateria", batteryPercentage: "Percentagem da Bateria",
        batterySwitchState: "Estado da Bateria", pressure: "Pressão", firmware: "Firmware", version: "Versão",
        inputs: "Entradas", digitalInputs: "Entradas Digitais", active: "Ativo", index: "Índice", counter: "Contador",
        speed: "Velocidade", battery: "Bateria", engine: "Motor", fuel: "Combustível", motion: "Movimento",
        maintenance: "Manutenção", doors: "Portas", tires: "Pneus", transmission: "Transmissão",
        controlUnit: "Unidade de Controlo", seatBelts: "Cintos", adas: "ADAS", dieselExhaustFluid: "AdBlue",
        cellInfo: "Informação da Célula", cellID: "ID da Célula", mnc: "MNC", mcc: "MCC", lac: "LAC",
        signalStrength: "Força do Sinal",
        ...groupTitles,
        moving: "Em movimento", rpm: "Regime do motor", engineFault: "Avaria do motor",
        checkEngineLightOnKm: "Distância com luz de avaria acesa", powerMode: "Modo de potência",
        ecoMode: "Modo económico", ecuSoftwareVersion: "Versão da unidade de controlo do motor",
        electricEngine: "Motor elétrico", hallSensorFault: "Avaria do sensor Hall", pwmSignalFault: "Avaria do sinal PWM",
        throttleFault: "Avaria do acelerador", additionalOdometers: "Distâncias adicionais",
        tractionBatteries: "Baterias de tração", code: "Código da bateria", batteryTemperature: "Temperatura da bateria",
        batteryControllerTemperature: "Temperatura do controlador da bateria", autonomyEco: "Autonomia em modo económico",
        autonomyNormal: "Autonomia em modo normal", autonomyPower: "Autonomia em modo de potência",
        charging: "Carregamento", cableOnBoard: "Cabo de carregamento a bordo", plugged: "Carregador ligado",
        stateOfHealth: "Estado de saúde da bateria", statusCode: "Código de estado", chargeCycles: "Ciclos de carga",
        batteriesCount: "Número de baterias", steeringLock: "Bloqueio da direção", oilToChange: "Mudança de óleo necessária",
        oilQualityPercentage: "Qualidade do óleo", oilQualitySensorFailure: "Avaria do sensor de qualidade do óleo",
        manufacturerCode: "Código do fabricante", genericAlarm: "Aviso de manutenção",
        diagnosticsErrorMask: "Máscara de erros de diagnóstico", obdErrorMask: "Máscara de erros OBD",
        centralLock: "Fecho central ativo", doorOpen: "Porta aberta", frontLeftDoor: "Porta dianteira esquerda",
        frontRightDoor: "Porta dianteira direita", frontLeftTire: "Pneu dianteiro esquerdo", frontRightTire: "Pneu dianteiro direito",
        rearLeftTire: "Pneu traseiro esquerdo", rearRightTire: "Pneu traseiro direito", lowTirePressure: "Pressão baixa",
        rows: "Filas", left: "Esquerda", center: "Centro", right: "Direita", fastened: "Cinto colocado",
        transmissionFault: "Avaria da transmissão", driveGear: "Mudança selecionada", gearEngaged: "Mudança engrenada",
        busCommunicationFault: "Avaria de comunicação", failure: "Avaria", esp: "Controlo de estabilidade (ESP)",
        abs: "Sistema antibloqueio (ABS)", lowLevel: "Nível baixo", levelPercentage: "Nível de AdBlue",
        privacyOn: "Privacidade ativa", temperatureSensors: "Sensores de temperatura", sensors: "Sensores",
      };
      return labelMap[segment] || segment.replace(/([a-z])([A-Z])/g, "$1 $2");
    },
    buildGroupCards(groupKey, items) {
      const cardGroups = new Map();
      const labelMaps = {
        gpsData: { hdop: "HDOP", satellitesCount: "Satélites", coordinates: "Coordenadas", altitude: "Altitude", speed: "Velocidade", odometer: "Odómetro" },
        gsmData: { cellInfo: "Informação da rede", signalStrength: "Sinal" },
        deviceDiagnostics: { batteryVoltage: "Bateria", batteryPercentage: "Bateria", batterySwitchState: "Bateria", pressure: "Pressão", temperatureSensors: "Temperatura", firmware: "Firmware", inputs: "Entradas" },
        vehicleDiagnostics: { battery: "Bateria", engine: "Motor", electricEngine: "Motor Elétrico", dashboardOn: "Painel", remoteDashboardOn: "Painel", odometer: "Odómetro", odometerRealTime: "Odómetro", additionalOdometers: "Odómetro", externalTemperature: "Temperatura", fuel: "Combustível", motion: "Movimento", tractionBattery: "Bateria de Tração", tractionBatteries: "Bateria de Tração", steeringLock: "Direção", maintenance: "Manutenção", doors: "Portas", tires: "Pneus", transmission: "Transmissão", controlUnit: "Unidade de Controlo", seatBelts: "Cintos", adas: "ADAS", dieselExhaustFluid: "AdBlue" },
      };
      const map = labelMaps[groupKey];
      if (!map) return [];
      items.forEach((item) => {
        const relativePath = item.path.split(".").slice(1).join(".");
        const root = relativePath.split(".")[0];
        const title = map[root] || this.translatePathSegment(root);
        if (!cardGroups.has(title)) cardGroups.set(title, []);
        const label = this.formatPath(relativePath.includes(".") ? relativePath.split(".").slice(1).join(".") : relativePath);
        cardGroups.get(title).push({ ...item, label });
      });
      return Array.from(cardGroups.entries()).map(([title, itemsList]) => ({ key: title, title, items: itemsList }));
    },
    tripColor(index) {
      return ["#2563eb", "#15803d", "#c2410c", "#7c3aed", "#be123c"][index % 5];
    },
    resizeMaps() {
      this.$nextTick(() => {
        if (this._isDestroyed) return;
        const gpsMap = this.$refs.gpsMap && this.$refs.gpsMap.mapObject;
        if (this.activeTab === 0 && gpsMap) gpsMap.invalidateSize({ pan: false });
        const tripsMap = this.$refs.tripsMap && this.$refs.tripsMap.mapObject;
        if (this.activeTab !== 1 || !tripsMap) return;
        tripsMap.invalidateSize({ pan: false });
        const points = this.routes.flatMap((route) => route.points);
        if (points.length) tripsMap.fitBounds(latLngBounds(points), { padding: [20, 20], maxZoom: 16 });
        else tripsMap.setView([39.5, -8], 6);
      });
    },
    backToList() {
      this.$closeCurrentTab({ name: 'targaTelematics' });
    }
  },
};
</script>

<style scoped>
.telematics-detail { min-width: 0; }
.detail-toolbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; margin-bottom: 1rem; }
.detail-toolbar h1 { margin-bottom: 0; overflow-wrap: anywhere; }
.detail-toolbar p { overflow-wrap: anywhere; }
.detail-subtitle, .data-note { color: #667085; }
.data-note { font-size: 0.85rem; line-height: 1.6; }
.vehicle-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1rem; margin: 1rem 0 1.5rem; }
.vehicle-summary :deep(.report-kpi__value) { white-space: normal; overflow: visible; overflow-wrap: anywhere; font-size: 1.4rem; }
.trip-summary { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.overview-layout { display: grid; grid-template-columns: minmax(0, 1.7fr) minmax(300px, 1fr); align-items: start; gap: 1.5rem; }
.vehicle-data, .location-panel { min-width: 0; }
.vehicle-data h2 { font-size: 1.25rem; margin-top: 0; }
.location-panel { border: 1px solid #e4e7ec; border-radius: 12px; padding: 1rem; position: sticky; top: 1rem; }
.location-heading { display: flex; align-items: center; gap: 0.6rem; color: #3969ac; }
.location-heading h2 { color: #1d2939; font-size: 1.25rem; margin: 0; }
.location-coordinates { display: flex; flex-direction: column; gap: 0.3rem; font-size: 0.8rem; margin-top: 0.75rem; color: #667085; }
.trip-filters { display: flex; flex-wrap: wrap; gap: 1rem; align-items: flex-end; margin-bottom: 1rem; border: 1px solid #e4e7ec; border-radius: 10px; padding: 1rem; }
.trip-filters label { display: block; margin-bottom: 0.5rem; }
.trip-filters > div { max-width: 100%; }
.map-container { height: 320px; width: 100%; position: relative; z-index: 0; }
.twin-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 230px), 1fr)); gap: 1rem; }
.twin-card { border: 1px solid #e4e7ec; padding: 1rem; border-radius: 8px; min-width: 0; }
.twin-card h3 { margin-top: 0; font-size: 1rem; color: #344054; }
.twin-card-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 0.25rem 0.75rem; padding: 0.5rem 0; border-bottom: 1px solid #f0f2f5; overflow-wrap: anywhere; }
.twin-card-row > span { color: #667085; }
.twin-card-row > strong { text-align: right; max-width: 18rem; overflow-wrap: anywhere; }
.twin-card-row small { grid-column: 1 / -1; color: #667085; font-size: 0.75rem; }
.trip-layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 2fr); gap: 1.5rem; }
.trip-layout > section { min-width: 0; }
.trip-list { max-height: 580px; overflow-y: auto; }
.trip-card { display: flex; align-items: flex-start; gap: 0.75rem; border: 1px solid #e4e7ec; padding: 1rem; margin-bottom: 0.75rem; cursor: pointer; border-radius: 8px; }
.trip-card.selected { border-color: #3969ac; background: #f3f7fc; }
.trip-card input { margin-top: 0.3rem; flex-shrink: 0; }
.trip-card span span { display: block; margin-top: 0.3rem; }
.trip-card > span { min-width: 0; overflow-wrap: anywhere; }
.trips-map { height: 480px; }
.route-legend { margin: 0.5rem 0; }
.route-color { display: inline-block; width: 1rem; height: 0.3rem; vertical-align: middle; margin-right: 0.4rem; }
a { text-decoration: underline; }
@media (max-width: 900px) {
  .overview-layout { grid-template-columns: minmax(0, 1fr); }
  .location-panel { position: static; grid-row: 1; }
  .vehicle-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .trip-layout { grid-template-columns: minmax(0, 1fr); }
  .trips-map { height: 360px; }
}
@media (max-width: 600px) {
  .vehicle-summary { grid-template-columns: minmax(0, 1fr); }
  .trip-filters > div { width: 100%; }
  .trip-filters :deep(.p-dropdown), .trip-filters :deep(.p-calendar) { width: 100%; }
  .twin-card-row { grid-template-columns: minmax(0, 1fr); }
  .twin-card-row > strong { text-align: left; }
}
</style>
